import { Stack, Typography } from "@mui/material";

import { OpenShiftCreated, OpenShiftDeleted, OpenShiftUpdated } from "~/api/eventLog/events";

import { IOpenShift, IStaffShift, IUnit } from "@/api";
import { ShiftIncentiveLevel } from "@/common/components";
import { darkGray } from "@/common/theming/colors";

const openShiftOriginCopy: {
  [key in
    | OpenShiftCreated.OpenShiftOrigin["type"]
    | OpenShiftUpdated.OpenShiftOrigin["type"]
    | OpenShiftDeleted.OpenShiftOrigin["type"]]: string;
} = {
  "bulk-save-action": "Manual update",
  "schedule-published": "Schedule published",
  "schedule-targets-updated": "Schedule targets updated",
  "schedule-updated": "Schedule updated",
  "approved-open-shift-request": "Open shift approved",
  "edit-open-shift": "Open shift updated",
  "principal-staff-job-changed": "Principal staff job changed",
};

export const OpenShiftDetails = ({
  openShift,
  origin,
  unitId,
}: {
  openShift?: Partial<
    Pick<
      IOpenShift,
      | "id"
      | "scheduleId"
      | "shiftType"
      | "durationSeconds"
      | "staffCategoryKey"
      | "shiftIncentiveLevelId"
      | "note"
      | "shiftCount"
      | "date"
      | "startDateTime"
    >
  >;
  origin:
    | OpenShiftCreated.OpenShiftOrigin
    | OpenShiftDeleted.OpenShiftOrigin
    | OpenShiftUpdated.OpenShiftOrigin;
  unitId?: IUnit["id"];
}) => {
  const originDetails = openShiftOriginCopy[origin.type];

  // Display from where the shift originated (approved OPS, manual update, etc.)
  let originRendered: string | undefined = undefined;
  if (originDetails) originRendered = originDetails;

  // Display the incentive level of the shift
  let shiftIncentiveLevel: JSX.Element | undefined = undefined;
  if (openShift?.shiftIncentiveLevelId)
    shiftIncentiveLevel = (
      <ShiftIncentiveLevel
        inline
        withDollarSign
        shiftIncentiveLevelId={openShift.shiftIncentiveLevelId}
        unitId={unitId}
      />
    );

  // Return everything in a stack, with `{title}: {content}` format
  return (
    <Stack direction={"column"} gap={0.4} mt={0.6}>
      {contentLine("IncentiveLevel", shiftIncentiveLevel)}
      {contentLine("Origin", originRendered)}
    </Stack>
  );
};

const contentLine = (title: string, content?: JSX.Element | string) =>
  content && (
    <Stack direction={"row"} gap={0.3} data-testid={title}>
      <Typography color={darkGray} fontSize="12px" display="inherit" alignItems={"center"}>
        <i>{title}</i> :{" "}
      </Typography>
      <Typography fontSize="12px" display="inherit" alignItems={"center"}>
        {content}
      </Typography>
    </Stack>
  );

export const OpenShiftDetailsDifferences = ({
  differences,
  origin,
  unitId,
}: {
  differences: OpenShiftUpdated.Differences;
  origin: OpenShiftUpdated.OpenShiftOrigin;
  unitId?: IUnit["id"];
}) => {
  const originDetails = openShiftOriginCopy[origin.type];

  // Display from where the shift originated (approved OPS, manual update, etc.)
  let originRendered: string | undefined = undefined;
  if (originDetails) originRendered = originDetails;

  // display if incentive level changed
  let incentiveLevel: JSX.Element | undefined = undefined;
  if (differences.shiftIncentiveLevelId) {
    const fromIncentiveLevelId = differences.shiftIncentiveLevelId.from;
    const toIncentiveLevelId = differences.shiftIncentiveLevelId.to;
    const renderIncentiveLevel = (incentiveLevelId?: IStaffShift["incentiveLevelId"]) =>
      incentiveLevelId ? (
        <ShiftIncentiveLevel
          inline
          withDollarSign
          shiftIncentiveLevelId={incentiveLevelId}
          unitId={unitId}
        />
      ) : (
        "no incentive"
      );

    const fromIncentiveLevel = renderIncentiveLevel(fromIncentiveLevelId);
    const toIncentiveLevel = renderIncentiveLevel(toIncentiveLevelId);

    incentiveLevel = (
      <>
        from {fromIncentiveLevel} to {toIncentiveLevel}
      </>
    );
  }

  // Display the shiftCount updated
  let shiftCount: JSX.Element | undefined = undefined;
  if (differences.shiftCount) {
    shiftCount = (
      <>
        from {differences.shiftCount.from} to "{differences.shiftCount.to}"
      </>
    );
  }

  // Display the note updated
  let note: JSX.Element | undefined = undefined;
  if (differences.note) {
    note = (
      <>
        from {differences.note.from ? `"${differences.note.from}"` : "no note"} {" to "}
        {differences.note.to ? `"${differences.note.to}"` : "no note"}
      </>
    );
  }

  // Return everything in a stack, with `{title}: {content}` format
  return (
    <Stack direction={"column"} gap={0.4} mt={0.6}>
      {updatedContentLine("incentiveLevel", incentiveLevel)}
      {updatedContentLine("note", note)}
      {updatedContentLine("shiftCount", shiftCount)}
      {contentLine("Origin", originRendered)}
    </Stack>
  );
};

const updatedContentLine = (title: string, content?: JSX.Element | string) =>
  content && (
    <Stack direction={"row"} gap={0.3} data-testid={title}>
      <Typography fontSize="12px" display="inherit" alignItems={"center"}>
        {content}
      </Typography>
    </Stack>
  );
